<template>
  <ModalsContainer />
  <VueFinalModal
    :model-value="isOpen"
    @update:model-value="$emit('update:isOpen', $event)"
    class="flex justify-center items-center"
    content-class="flex flex-col max-w-xl mx-4 p-4 bg-base-100 border border-base-300 rounded-lg space-y-2"
  >
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-base-content">Change Password</h2>
      <button 
        @click="closeModal"
        class="btn btn-ghost btn-sm btn-circle"
      >
        <v-icon name="io-close" />
      </button>
    </div>

    <form @submit.prevent="handlePasswordUpdate" class="space-y-4">
      <div class="space-y-4">
        <XInput
          v-model="password"
          id="password"
          label="New Password"
          type="password"
          requiredIconHidden="true"
          :required="true"
          minLength="8"
        />

        <XInput
          v-model="confirmedPassword"
          id="confirm-password"
          label="Confirm Password"
          type="password"
          requiredIconHidden="true"
          :required="true"
          minLength="8"
        />
      </div>

      <Alert
        v-if="alertMessage"
        :isShowing="alertMessage"
        :alertMessage="alertMessage"
        :type="alertType"
      />

      <div class="flex justify-end gap-3 pt-4">
        <XButton 
          type="button" 
          color="ghost" 
          @click="closeModal"
        >
          Cancel
        </XButton>
        <XButton
          type="submit"
          color="primary"
          :isLoading="isLoading"
          :isDisabled="!password || !confirmedPassword"
        >
          Change Password
        </XButton>
      </div>
    </form>
  </VueFinalModal>
</template>

<script setup>
import { ref, watch } from 'vue'
import { VueFinalModal, ModalsContainer } from 'vue-final-modal'
import { useMutation } from '@vue/apollo-composable'
import UPDATE_USER_MUTATION from '@/graphql/User/UpdateUserMutation.gql'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  userId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:isOpen', 'success'])

// Form state
const password = ref('')
const confirmedPassword = ref('')
const alertMessage = ref('')
const alertType = ref('')

// GraphQL mutation
const { mutate: updateUser, loading: isLoading } = useMutation(UPDATE_USER_MUTATION)

// Password validation
const confirmPasswordSecurity = (value) => {
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/
  return passwordPattern.test(value)
}

// Handle password update
const handlePasswordUpdate = async () => {
  alertMessage.value = ''
  alertType.value = ''

  if (password.value !== confirmedPassword.value) {
    alertMessage.value = 'Passwords do not match'
    alertType.value = 'error'
    return
  }

  if (!confirmPasswordSecurity(password.value)) {
    alertMessage.value = 'Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one special character, and one number.'
    alertType.value = 'error'
    return
  }

  try {
    await updateUser({
      input: {
        id: props.userId,
        password: password.value,
      },
    })
    
    alertMessage.value = 'Password updated successfully.'
    alertType.value = 'success'
    
    // Reset form after success
    setTimeout(() => {
      resetForm()
      closeModal()
      emit('success')
    }, 2000)
    
  } catch (error) {
    console.error('Password update error:', error)
    alertMessage.value = 'Failed to update password. Please try again.'
    alertType.value = 'error'
  }
}

// Reset form
const resetForm = () => {
  password.value = ''
  confirmedPassword.value = ''
  alertMessage.value = ''
  alertType.value = ''
}

// Close modal
const closeModal = () => {
  resetForm()
  emit('update:isOpen', false)
}

// Watch for modal closing to reset form
watch(() => props.isOpen, (newValue) => {
  if (!newValue) {
    resetForm()
  }
})
</script>